import {
  resolveAssetUrl,
  type ImageLike,
} from '@sportninja/common/utils/assetUrl'
import {
  buildLandingRegistry,
  getLandingConfig,
} from '@sportninja/common/features/notifications/utils/landingRegistry'
import type {
  LandingNotification,
  NotificationLandingAction,
} from '@sportninja/common/features/notifications/types/landing'
import { resolveHeaderTeamUid } from '@sportninja/common/features/notifications/utils/notificationHeader'
import { getApiErrorMessage } from '@sportninja/common/features/notifications/utils/apiErrorMessage'
import { useState } from 'react'
import { useAvailabilityRequestActions } from './useAvailabilityRequestActions'
import { useNotification } from './useNotification'
import { useTeamRecord } from './useTeamRecord'

export interface NotificationDetailViewModel {
  notification?: LandingNotification
  isLoading: boolean
  isError: boolean
  /** The API's own error message for the failed fetch, when it supplied one. */
  errorMessage?: string
  title: string
  body: string
  messageText: string
  logoUrl?: string
  primaryAction?: NotificationLandingAction
  secondaryAction?: NotificationLandingAction
  hasActions: boolean
  busy: boolean
  runAction: (action: NotificationLandingAction) => Promise<ActionResult>
}

/** Outcome of running a notification action, with the API's message on failure. */
export interface ActionResult {
  ok: boolean
  errorMessage?: string
}

/**
 * Orchestrates the notification detail page: fetches the notification, reads
 * its actions from the landing registry, and independently resolves the header
 * team logo from the payload. Nothing here is spare-specific.
 */
export const useNotificationDetail = (
  notificationId?: string
): NotificationDetailViewModel => {
  'use memo'

  const {
    data: notification,
    isLoading,
    isError,
    errorMessage,
  } = useNotification(notificationId)
  const { setAvailability, isPending } = useAvailabilityRequestActions()
  const [running, setRunning] = useState(false)

  const registry = buildLandingRegistry({ setAvailability })
  const config = notification
    ? getLandingConfig(registry, notification.type)
    : undefined

  const teamUid = resolveHeaderTeamUid(notification)
  const { data: team } = useTeamRecord(teamUid)

  const runAction = async (
    action: NotificationLandingAction
  ): Promise<ActionResult> => {
    if (!notification) {
      return { ok: false }
    }
    setRunning(true)
    try {
      await action.run(notification)
      return { ok: true }
    } catch (error) {
      // Return the API's own message alongside the outcome rather than storing
      // it in state — the caller awaits this promise and would otherwise read a
      // stale value from the previous render.
      return { ok: false, errorMessage: getApiErrorMessage(error) }
    } finally {
      setRunning(false)
    }
  }

  return {
    notification,
    isLoading,
    isError,
    errorMessage,
    title: notification?.data?.title ?? '',
    body: notification?.data?.body ?? '',
    messageText: notification?.data?.message ?? '',
    logoUrl: team?.image ? resolveAssetUrl(team.image as ImageLike) : undefined,
    primaryAction: config?.primaryAction,
    secondaryAction: config?.secondaryAction,
    hasActions: Boolean(config?.primaryAction || config?.secondaryAction),
    busy: running || isPending,
    runAction,
  }
}
